import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useMarketingAddon } from '../contexts/MarketingAddonContext';
import Logo from './Logo';
import BrandLogo from './BrandLogo';
import {
  Home,
  MessageSquare,
  BarChart3,
  Users,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Package,
  ShoppingCart,
  Receipt,
  CreditCard,
  UserCircle,
  TrendingUp,
  BookOpen,
  Building2,
  Database,
  Palette,
  PenLine,
  List,
  Clock,
  Landmark,
  Crown,
  ShoppingBag,
  FileBarChart,
  ArrowDownLeft,
  ArrowUpRight,
  ArrowLeftRight,
  FileText,
  Wallet,
  RotateCcw,
  TrendingDown,
  GraduationCap,
  Briefcase,
  ClipboardList,
  LayoutDashboard,
  Factory,
  Truck,
  MapPin,
  BoxSelect,
  FileSpreadsheet,
  CalendarClock,
  PackageOpen,
  History,
  Network,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useConnectedBusinessesEnabled } from '../lib/connectedBusinesses';

/* ── Category icon colors (muted/professional) ── */
const IC = {
  sales:    '#87A878', // sage green
  purchase: '#C98B6B', // dusty orange
  accounts: '#5B7A99', // steel blue
  school:   '#9B8AB8', // soft purple
  hr:       '#5E8B87', // muted teal
  ai:       '#C9A86B', // warm amber
  settings: '#6B7280', // slate grey
  mfg:      '#7E6BA4', // soft indigo
};

const ERP_CHILDREN = [
  { to: '/erp', icon: BookOpen, label: 'Overview', exact: true, iconColor: IC.accounts },
];

const FINANCE_CHILDREN = [
  { to: '/outstanding', icon: TrendingDown, label: 'Outstanding', iconColor: IC.accounts },
  { to: '/erp/journal', icon: PenLine, label: 'Journal', iconColor: IC.accounts },
  { to: '/erp/ledger', icon: List, label: 'Ledger', iconColor: IC.accounts },
  { to: '/erp/aging', icon: Clock, label: 'Aging', iconColor: IC.accounts },
  { to: '/erp/gst', icon: Receipt, label: 'GST', iconColor: IC.accounts },
  { to: '/erp/bank', icon: Landmark, label: 'Bank Sync', iconColor: IC.accounts },
  { to: '/vouchers/payment', icon: ArrowDownLeft, label: 'Payment Voucher', iconColor: IC.accounts },
  { to: '/vouchers/receipt', icon: ArrowUpRight, label: 'Receipt Voucher', iconColor: IC.accounts },
  { to: '/vouchers/contra', icon: ArrowLeftRight, label: 'Contra Voucher', iconColor: IC.accounts },
  { to: '/vouchers', icon: FileText, label: 'Vouchers List', exact: true, iconColor: IC.accounts },
  { to: '/notes/credit', icon: RotateCcw, label: 'Credit Notes', iconColor: IC.accounts },
  { to: '/notes/debit', icon: RotateCcw, label: 'Debit Notes', iconColor: IC.accounts },
];

const SCHOOL_CHILDREN = [
  { to: '/school/dashboard', icon: LayoutDashboard, label: 'School Dashboard', iconColor: IC.school },
  { to: '/school/students', icon: GraduationCap, label: 'Students', iconColor: IC.school },
  { to: '/school/employees', icon: Briefcase, label: 'Employees', iconColor: IC.school },
  { to: '/school/fee-plans', icon: ClipboardList, label: 'Fee Plans', iconColor: IC.school },
  { to: '/school/fees', icon: Receipt, label: 'Fees', iconColor: IC.school },
  { to: '/school/billing-logs', icon: FileBarChart, label: 'Billing Logs', iconColor: IC.school },
];

const MFG_CHILDREN = [
  { to: '/mfg', icon: LayoutDashboard, label: 'Dashboard', exact: true, iconColor: IC.mfg },
  { to: '/mfg/orders', icon: ClipboardList, label: 'Client Orders', iconColor: IC.mfg },
  { to: '/mfg/production', icon: Factory, label: 'Production Entry', iconColor: IC.mfg },
  { to: '/mfg/dispatch', icon: Truck, label: 'Dispatch', iconColor: IC.mfg },
  { to: '/mfg/reports/dpr', icon: FileSpreadsheet, label: 'DPR', iconColor: IC.mfg },
  { to: '/mfg/reports/monthly', icon: CalendarClock, label: 'Monthly Plan', iconColor: IC.mfg },
  { to: '/mfg/reports/fg-stock', icon: PackageOpen, label: 'FG Stock', iconColor: IC.mfg },
  { to: '/mfg/reports/dispatch-history', icon: History, label: 'Dispatch History', iconColor: IC.mfg },
  { to: '/mfg/reports/production-history', icon: History, label: 'Production History', iconColor: IC.mfg },
  { to: '/mfg/units', icon: BoxSelect, label: 'Units', iconColor: IC.mfg },
  { to: '/mfg/skus', icon: Package, label: 'SKUs', iconColor: IC.mfg },
  { to: '/mfg/destinations', icon: MapPin, label: 'Destinations', iconColor: IC.mfg },
  { to: '/mfg/opening-stock', icon: Database, label: 'Opening Stock', iconColor: IC.mfg },
];

const REPORT_CHILDREN = [
  { to: '/reports/aging', icon: Clock, label: 'Aging', iconColor: IC.accounts },
  { to: '/reports/tax', icon: Receipt, label: 'Tax (GST/VAT)', iconColor: IC.accounts },
  { to: '/reports/gstr1', icon: FileBarChart, label: 'GSTR-1 Export', iconColor: IC.accounts },
  { to: '/reports/trial-balance', icon: BookOpen, label: 'Trial Balance', iconColor: IC.accounts },
  { to: '/reports/pnl', icon: TrendingUp, label: 'Profit & Loss', iconColor: IC.accounts },
  { to: '/reports/balance-sheet', icon: Landmark, label: 'Balance Sheet', iconColor: IC.accounts },
];

export default function Sidebar() {
  const { token, user, tenant, logout } = useAuth();
  const { brand, profile } = useTenant();

  // Phase 23 Step 3 — School & Club is shown only for school tenants.
  // The login payload carries a slim tenant, so read business_type from
  // /tenants/me.
  const [businessType, setBusinessType] = useState(tenant?.business_type || null);
  const [mfgEnabled, setMfgEnabled] = useState(false);
  useEffect(() => {
    if (!token) return;
    let alive = true;
    api.tenantMe(token)
      .then(t => { if (alive) setBusinessType(t?.business_type || 'retail'); })
      .catch(() => {});
    const BASE = process.env.REACT_APP_BACKEND_URL || '';
    Promise.all([
      api.tenantMe(token),
      fetch(`${BASE}/api/features/check?feature=manufacturing_module`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => null),
    ]).then(([t, fc]) => {
      if (!alive) return;
      setBusinessType(t?.business_type || 'retail');
      const isMfgType = t?.business_type === 'manufacturing';
      const hasOverride = fc?.status === 'allowed' && (fc?.reason === 'admin_on' || fc?.reason === 'admin_override_on');
      setMfgEnabled(isMfgType || hasOverride);
    }).catch(() => {});
    return () => { alive = false; };
    // Re-read when the industry changes (School / Coaching unlocks School & Club).
  }, [token, profile?.industry]);
  const isSchoolTenant = businessType === 'school' || businessType === 'coaching';
  const { activated: marketingActive } = useMarketingAddon();
  const cbEnabled = useConnectedBusinessesEnabled();
  const isOwner = String(user?.role || '').toLowerCase() === 'owner';
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const initials = user?.name?.split(' ').slice(0, 2).map(s => s[0]).join('').toUpperCase() || '?';

  const isErpRoute = location.pathname === '/erp' || location.pathname.startsWith('/erp/');
  const [erpExpanded, setErpExpanded] = useState(isErpRoute);

  const isFinanceRoute = location.pathname.startsWith('/erp/') || location.pathname.startsWith('/vouchers') || location.pathname.startsWith('/notes/') || location.pathname.startsWith('/outstanding');
  const [financeExpanded, setFinanceExpanded] = useState(isFinanceRoute);

  const isReportsRoute = location.pathname.startsWith('/reports');
  const [reportsExpanded, setReportsExpanded] = useState(isReportsRoute);

  const isSchoolRoute = location.pathname.startsWith('/school');
  const [schoolExpanded, setSchoolExpanded] = useState(isSchoolRoute);

  const isMfgRoute = location.pathname.startsWith('/mfg');
  const [mfgExpanded, setMfgExpanded] = useState(isMfgRoute);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const BOARDROOM_CHILDREN = [
    { to: '/assistant', icon: MessageSquare, label: 'AI Assistant', iconColor: IC.ai },
    { to: '/managers/ceo', icon: Users, label: 'AI Managers', iconColor: IC.ai },
    { to: '/insights', icon: BarChart3, label: 'Insights', iconColor: IC.ai },
    { to: '/sales-strategy', icon: TrendingUp, label: 'AI Sales Strategy', iconColor: IC.ai },
  ];

  const isBoardroomRoute = location.pathname === '/boardroom'
    || ['/assistant', '/managers', '/insights', '/sales-strategy'].some(
      p => location.pathname === p || location.pathname.startsWith(p + '/')
    );
  const [boardroomExpanded, setBoardroomExpanded] = useState(isBoardroomRoute);

  // Build dynamic nav items — marketing entry conditionally added
  const items = [
    { to: '/home', icon: Home, label: 'Home', iconColor: IC.settings },
    { type: 'boardroom-group' },
    { type: 'finance-group' },
    ...(isSchoolTenant ? [{ type: 'school-group' }] : []),
    ...(mfgEnabled ? [{ type: 'mfg-group' }] : []),
    'divider',
    { to: '/inventory', icon: Package, label: 'Inventory', iconColor: IC.sales },
    { to: '/sales', icon: ShoppingCart, label: 'Sales', iconColor: IC.sales },
    { to: '/purchase-orders', icon: ShoppingBag, label: 'Purchases', iconColor: IC.purchase },
    { to: '/expenses', icon: Receipt, label: 'Expenses', iconColor: IC.purchase },
    'divider',
    { type: 'erp-group' },
    { type: 'reports-group' },
    { to: '/multi-branch', icon: Building2, label: 'Multi-Branch Ledger', accent: '#F43F5E', iconColor: IC.accounts },
    { to: '/integrations', icon: Database, label: 'Data Bridge', accent: '#84CC16', iconColor: IC.settings },
    ...(marketingActive ? [{ to: '/brand-assets', icon: Palette, label: 'AI Design & Marketing', accent: '#DB2777', iconColor: IC.ai }] : []),
    'divider',
    { to: '/billing', icon: CreditCard, label: 'Billing', iconColor: IC.settings },
    { to: '/profile', icon: UserCircle, label: 'Profile', iconColor: IC.settings },
    ...(cbEnabled === true && isOwner ? [{ to: '/connected-businesses/settings', icon: Network, label: 'Connected Businesses', iconColor: IC.settings }] : []),
  ];

  return (
    <aside
      data-testid="app-sidebar"
      className={`flex flex-col h-screen bg-white border-r border-gray-200 shadow-sm transition-all duration-300 ${collapsed ? 'w-[72px]' : 'w-[260px]'}`}
    >
      {/* Logo — brand logo when uploaded, else 1000AD */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100" data-testid="sidebar-logo">
        {brand?.brand_logo_url ? (
          <BrandLogo size={collapsed ? 28 : 44} rounded className="flex-shrink-0" theme="light" />
        ) : (
          !collapsed && <Logo size="lg" theme="light" />
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
        {items.map((item, i) => {
          if (item === 'divider') return <div key={`div-${i}`} className="h-px bg-gray-100 my-2 mx-2" />;

          if (item.type === 'boardroom-group') {
            return (
              <div key="boardroom-group" data-testid="boardroom-sidebar-group">
                {/* Group header — label click navigates, chevron toggles children */}
                <div
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all group ${
                    isBoardroomRoute ? 'bg-purple-50 text-gray-900' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <button
                    onClick={() => navigate('/boardroom')}
                    data-testid="nav-ai-boardroom"
                    className="flex items-center gap-3 flex-1 min-w-0 text-left"
                  >
                    <Crown size={20} strokeWidth={1.8} className="flex-shrink-0" style={{ color: IC.ai }} />
                    {!collapsed && <span className="text-sm font-semibold truncate">AI Boardroom</span>}
                  </button>
                  {!collapsed && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setBoardroomExpanded(v => !v); }}
                      data-testid="nav-ai-boardroom-chevron"
                      className="p-1 rounded-md hover:bg-gray-200 transition-colors flex-shrink-0"
                      aria-label={boardroomExpanded ? 'Collapse AI hubs' : 'Expand AI hubs'}
                    >
                      <ChevronDown size={14} className={`transition-transform ${boardroomExpanded || isBoardroomRoute ? 'rotate-180' : ''}`} style={{ color: IC.ai }} />
                    </button>
                  )}
                </div>
                {/* Nested AI hub children */}
                {(boardroomExpanded || isBoardroomRoute) && !collapsed && (
                  <div className="ml-5 mt-0.5 space-y-0.5 border-l border-gray-200 pl-3">
                    {BOARDROOM_CHILDREN.map(child => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        data-testid={`nav-ai-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className={({ isActive }) =>
                          `flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-all text-xs font-semibold ${
                            isActive ? 'bg-purple-50 text-brand-purple' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-50'
                          }`
                        }
                      >
                        <child.icon size={14} strokeWidth={1.8} className="flex-shrink-0" style={{ color: child.iconColor }} />
                        <span>{child.label}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          if (item.type === 'finance-group') {
            return (
              <div key="finance-group" data-testid="finance-sidebar-group">
                <div
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all group ${
                    isFinanceRoute ? 'bg-teal-50 text-gray-900' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <button
                    onClick={() => navigate('/erp/journal')}
                    data-testid="nav-finance-accounting"
                    className="flex items-center gap-3 flex-1 min-w-0 text-left"
                  >
                    <Wallet size={20} strokeWidth={1.8} className="flex-shrink-0" style={{ color: IC.accounts }} />
                    {!collapsed && <span className="text-sm font-semibold truncate">Finance & Accounting</span>}
                  </button>
                  {!collapsed && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setFinanceExpanded(v => !v); }}
                      data-testid="nav-finance-chevron"
                      className="p-1 rounded-md hover:bg-gray-200 transition-colors flex-shrink-0"
                      aria-label={financeExpanded ? 'Collapse Finance' : 'Expand Finance'}
                    >
                      <ChevronDown size={14} className={`transition-transform ${financeExpanded || isFinanceRoute ? 'rotate-180' : ''}`} style={{ color: IC.accounts }} />
                    </button>
                  )}
                </div>
                {(financeExpanded || isFinanceRoute) && !collapsed && (
                  <div className="ml-5 mt-0.5 space-y-0.5 border-l border-gray-200 pl-3">
                    {FINANCE_CHILDREN.map(child => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        end={child.exact}
                        data-testid={`nav-fin-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className={({ isActive }) =>
                          `flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-all text-xs font-semibold ${
                            isActive ? 'bg-teal-50 text-teal-700' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-50'
                          }`
                        }
                      >
                        <child.icon size={14} strokeWidth={1.8} className="flex-shrink-0" style={{ color: child.iconColor }} />
                        <span>{child.label}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          if (item.type === 'school-group') {
            return (
              <div key="school-group" data-testid="school-sidebar-group">
                <div
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all group ${
                    isSchoolRoute ? 'bg-indigo-50 text-gray-900' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <button
                    onClick={() => navigate('/school/dashboard')}
                    data-testid="nav-school-club"
                    className="flex items-center gap-3 flex-1 min-w-0 text-left"
                  >
                    <GraduationCap size={20} strokeWidth={1.8} className="flex-shrink-0" style={{ color: IC.school }} />
                    {!collapsed && <span className="text-sm font-semibold truncate">School & Club</span>}
                  </button>
                  {!collapsed && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setSchoolExpanded(v => !v); }}
                      data-testid="nav-school-chevron"
                      className="p-1 rounded-md hover:bg-gray-200 transition-colors flex-shrink-0"
                      aria-label={schoolExpanded ? 'Collapse School' : 'Expand School'}
                    >
                      <ChevronDown size={14} className={`transition-transform ${schoolExpanded || isSchoolRoute ? 'rotate-180' : ''}`} style={{ color: IC.school }} />
                    </button>
                  )}
                </div>
                {(schoolExpanded || isSchoolRoute) && !collapsed && (
                  <div className="ml-5 mt-0.5 space-y-0.5 border-l border-gray-200 pl-3">
                    {SCHOOL_CHILDREN.map(child => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        data-testid={`nav-school-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className={({ isActive }) =>
                          `flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-all text-xs font-semibold ${
                            isActive ? 'bg-indigo-50 text-indigo-700' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-50'
                          }`
                        }
                      >
                        <child.icon size={14} strokeWidth={1.8} className="flex-shrink-0" style={{ color: child.iconColor }} />
                        <span>{child.label}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          if (item.type === 'mfg-group') {
            return (
              <div key="mfg-group" data-testid="mfg-sidebar-group">
                <div className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all group ${isMfgRoute ? 'bg-purple-50 text-gray-900' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}>
                  <button onClick={() => navigate('/mfg')} data-testid="nav-manufacturing" className="flex items-center gap-3 flex-1 min-w-0 text-left">
                    <Factory size={20} strokeWidth={1.8} className="flex-shrink-0" style={{ color: IC.mfg }} />
                    {!collapsed && <span className="text-sm font-semibold truncate">Manufacturing</span>}
                  </button>
                  {!collapsed && (
                    <button onClick={(e) => { e.stopPropagation(); setMfgExpanded(v => !v); }} data-testid="nav-mfg-chevron" className="p-1 rounded-md hover:bg-gray-200 transition-colors flex-shrink-0">
                      <ChevronDown size={14} className={`transition-transform ${mfgExpanded || isMfgRoute ? 'rotate-180' : ''}`} style={{ color: IC.mfg }} />
                    </button>
                  )}
                </div>
                {(mfgExpanded || isMfgRoute) && !collapsed && (
                  <div className="ml-5 mt-0.5 space-y-0.5 border-l border-gray-200 pl-3">
                    {MFG_CHILDREN.map(child => (
                      <NavLink key={child.to} to={child.to}
                        data-testid={`nav-mfg-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className={({ isActive }) => `flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-all text-xs font-semibold ${isActive ? 'bg-purple-50 text-purple-700' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-50'}`}>
                        <child.icon size={14} strokeWidth={1.8} className="flex-shrink-0" style={{ color: child.iconColor }} />
                        <span>{child.label}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          if (item.type === 'erp-group') {
            return (
              <div key="erp-group" data-testid="erp-sidebar-group">
                {/* Group header */}
                <button
                  onClick={() => { setErpExpanded(v => !v); if (!isErpRoute) navigate('/erp'); }}
                  data-testid="nav-smart-erp-engine"
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all group ${
                    isErpRoute ? 'bg-blue-50 text-gray-900' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <BookOpen size={20} strokeWidth={1.8} className="flex-shrink-0" style={{ color: IC.accounts }} />
                  {!collapsed && (
                    <>
                      <span className="text-sm font-semibold flex-1 text-left">Smart ERP</span>
                      <ChevronDown size={14} className={`transition-transform ${erpExpanded || isErpRoute ? 'rotate-180' : ''}`} style={{ color: IC.accounts }} />
                    </>
                  )}
                </button>
                {/* Children */}
                {(erpExpanded || isErpRoute) && !collapsed && (
                  <div className="ml-5 mt-0.5 space-y-0.5 border-l border-gray-200 pl-3">
                    {ERP_CHILDREN.map(child => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        end={child.exact}
                        data-testid={`nav-erp-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className={({ isActive }) =>
                          `flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-all text-xs font-semibold ${
                            isActive ? 'bg-blue-50 text-blue-600' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-50'
                          }`
                        }
                      >
                        <child.icon size={14} strokeWidth={1.8} className="flex-shrink-0" style={{ color: child.iconColor }} />
                        <span>{child.label}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          if (item.type === 'reports-group') {
            return (
              <div key="reports-group" data-testid="reports-sidebar-group">
                <button
                  onClick={() => { setReportsExpanded(v => !v); if (!isReportsRoute) navigate('/reports/aging'); }}
                  data-testid="nav-reports"
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all group ${
                    isReportsRoute ? 'bg-amber-50 text-gray-900' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <FileBarChart size={20} strokeWidth={1.8} className="flex-shrink-0" style={{ color: IC.accounts }} />
                  {!collapsed && (
                    <>
                      <span className="text-sm font-semibold flex-1 text-left">Reports</span>
                      <ChevronDown size={14} className={`transition-transform ${reportsExpanded || isReportsRoute ? 'rotate-180' : ''}`} style={{ color: IC.accounts }} />
                    </>
                  )}
                </button>
                {(reportsExpanded || isReportsRoute) && !collapsed && (
                  <div className="ml-5 mt-0.5 space-y-0.5 border-l border-gray-200 pl-3">
                    {REPORT_CHILDREN.map(child => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        data-testid={`nav-report-${child.label.toLowerCase().replace(/[\s()\/]+/g, '-')}`}
                        className={({ isActive }) =>
                          `flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-all text-xs font-semibold ${
                            isActive ? 'bg-amber-50 text-amber-700' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-50'
                          }`
                        }
                      >
                        <child.icon size={14} strokeWidth={1.8} className="flex-shrink-0" style={{ color: child.iconColor }} />
                        <span>{child.label}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          return (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all group ${
                  isActive
                    ? 'bg-gray-100 text-gray-900 font-semibold'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                }`
              }
            >
              <item.icon size={20} strokeWidth={1.8} className="flex-shrink-0" style={{ color: item.iconColor }} />
              {!collapsed && <span className="text-sm font-semibold">{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(v => !v)}
        data-testid="sidebar-collapse-btn"
        className="mx-3 mb-2 flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-400 hover:text-gray-700 transition-colors"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
      </button>

      {/* User section */}
      <div className="border-t border-gray-100 p-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-purple to-brand-teal flex items-center justify-center flex-shrink-0">
            <span className="text-white text-xs font-bold">{initials}</span>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-gray-900 text-sm font-semibold truncate">{user?.name}</p>
              <p className="text-gray-400 text-xs truncate">{tenant?.name}</p>
            </div>
          )}
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-red-500 transition-colors"
            aria-label="Logout"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
}
